import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Card, CardContent, CardHeader, Stack } from '@mui/material'
import { Select, MultiLineChart, ChartExportWrapper } from 'components'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import {
  getTabByKey,
  granularityOptions,
  prettyAttr,
  TAB_KEYS,
} from '../../utils/consts'
import {
  selectChartData,
  selectChartStatus,
} from 'store/selectors/dataAnalysisSelectors'
import {
  patchDraftFilters,
  applyAndFetchChart,
} from 'store/slices/dataAnalysisSlice'
import { TIME_GRANULARITY, TCostTrendData } from 'types/models/DataAnalysis'
import { RootState } from 'store/store'
import { toCostTrendChartData } from '../../transforms'
import * as sxStyles from '../tabs.styles'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import normalizeDataset from '../../utils/normalizeDataset'

export const CostTrend = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.COST_TRENDS))
  const status = useSelector(selectChartStatus(TAB_KEYS.COST_TRENDS))
  const currentTab = getTabByKey(TAB_KEYS.COST_TRENDS, 'blends')

  const draftFilters = useSelector(
    (state: RootState) => state.dataAnalysis.byKey[TAB_KEYS.COST_TRENDS]?.draft
  )
  const granularity = draftFilters?.time_granularity ?? TIME_GRANULARITY.DAILY

  useEffect(() => {
    if (!draftFilters?.time_granularity) {
      dispatch(
        patchDraftFilters({
          key: TAB_KEYS.COST_TRENDS,
          patch: { time_granularity: TIME_GRANULARITY.DAILY },
        })
      )
    }
  }, [dispatch, draftFilters])

  const data = useMemo(
    () => normalizeDataset<TCostTrendData>(rawData as any),
    [rawData]
  )
  const { rows, series } = useMemo(() => toCostTrendChartData(data), [data])
  const hasAnyData = rows.length > 0

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.COST_TRENDS}
        />
      }
      tabKey={TAB_KEYS.COST_TRENDS}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.COST_TRENDS }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.COST_TRENDS}>
        <Stack alignItems="flex-end" width="100%">
          <Box
            sx={{
              paddingTop: 0.5,
              width: 250,
              paddingBottom: 0,
              marginBottom: 0,
            }}
          >
            <Select
              size="small"
              fullWidth
              label="Period"
              options={granularityOptions}
              value={granularity}
              onChange={(e) => {
                const g = e.target.value as TIME_GRANULARITY
                dispatch(
                  patchDraftFilters({
                    key: TAB_KEYS.COST_TRENDS,
                    patch: { time_granularity: g },
                  })
                )
                dispatch(applyAndFetchChart({ key: TAB_KEYS.COST_TRENDS }))
              }}
            />
          </Box>
        </Stack>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <Card
              data-exportable-chart="true"
              variant="outlined"
              sx={{
                ...sxStyles.parametersComparisonCard,
                width: '100%',
              }}
            >
              <CardHeader
                title={prettyAttr('Cost Trend')}
                titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
              />
              <CardContent sx={sxStyles.parametersComparisonCardContent}>
                <Box sx={{ flex: 1 }}>
                  <ChartExportWrapper title="Cost Trend">
                    <MultiLineChart
                      data={rows}
                      series={series}
                      xDataKey="period"
                    />
                  </ChartExportWrapper>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
